import {
  FeeScheduleScope,
  type IFeeSchedule,
  type IFeeScheduleGroup,
} from '@principle-theorem/principle-core/interfaces';
import {
  isSameRef,
  shareReplayCold,
  type IReffable,
  type WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { combineLatest, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Shared selection logic for fee schedule dropdowns.
 *
 * Filters the available groups by the practice/staffer fee schedules feature
 * flag, builds the "Auto" placeholder label for the resolved default
 * schedule, and flags a selected schedule as stale when it is no longer
 * available in the visible groups.
 */
export class FeeScheduleSelectorBloc<
  Schedule extends IReffable<IFeeSchedule>,
> {
  groups$: Observable<IFeeScheduleGroup[]>;
  autoLabel$: Observable<string>;
  staleSchedule$: Observable<Schedule | undefined>;

  constructor(
    allGroups$: Observable<IFeeScheduleGroup[]>,
    practiceStafferSchedulesEnabled$: Observable<boolean>,
    defaultFeeSchedule$: Observable<WithRef<IFeeSchedule>>,
    selected$: Observable<Schedule | undefined>
  ) {
    this.groups$ = combineLatest([
      allGroups$,
      practiceStafferSchedulesEnabled$,
    ]).pipe(
      map(([groups, schedulesEnabled]) =>
        schedulesEnabled
          ? groups
          : groups.filter(
              (group) => group.scope === FeeScheduleScope.Organisation
            )
      ),
      shareReplayCold()
    );

    this.autoLabel$ = combineLatest([defaultFeeSchedule$, this.groups$]).pipe(
      map(([feeSchedule, groups]) => {
        const group = groups.find((scheduleGroup) =>
          scheduleGroup.schedules.some((schedule) =>
            isSameRef(schedule, feeSchedule)
          )
        );
        const parts = compact([group?.name, feeSchedule.name]);
        return `Auto - ${parts.join(' - ')}`;
      })
    );

    this.staleSchedule$ = combineLatest([selected$, this.groups$]).pipe(
      map(([selected, groups]) => this._staleSchedule(selected, groups))
    );
  }

  private _staleSchedule(
    selected: Schedule | undefined,
    groups: IFeeScheduleGroup[]
  ): Schedule | undefined {
    if (!selected) {
      return undefined;
    }
    const allSchedules = groups.flatMap((group) => group.schedules);
    const isInGroups = allSchedules.some((schedule) =>
      isSameRef(schedule, selected)
    );
    return isInGroups ? undefined : selected;
  }
}
